import React from "react"
import { graphql } from "gatsby"
import { withPrismicPreview } from "gatsby-plugin-prismic-previews"
import styled from "styled-components"

import Layout from "../components/layout"
import Seo from "../components/seo"

// Import Shared components
import { StandardContainer, StyledHeading } from "../components/common"
import { BlogHeader } from "../components/blog/blogHeader"
import { SliceZone } from "../components/slices"
import { TagNav, BlogShare, Radio } from "../components/blog"
import { OtherBlogs } from "../components/blog/otherBlogs"

//
const StyledBlogFooter = styled(StandardContainer)`
  display: grid;
  grid-row-gap: 2rem;
  padding: 0rem 2rem 4rem 2rem;

  @media ${(props) => props.theme.breakpoints.medium} {
    grid-template-columns: 1.5fr auto;
  }
`

const AButton = styled.a`
  font-size: 12px;
  color: ${(props) => props.theme.colors.buttonTextColor};
  background: ${(props) => props.theme.colors.brandColor};
  border-radius: 100rem;
  padding: 0.6rem 2.3rem;
  line-height: 1.5;
  border: none;
  display: flex;
  font-weight: 700;
  margin-top: 20px;
  text-decoration: none;
  cursor: pointer;
  width: fit-content;
  white-space: nowrap;
`

const BlogPost = ({ data }) => {
  const {
    title,
    category,
    meta_title,
    meta_description,
    keywords,
    body,
    radio_url,
    other_blogs,
    podcast_links,
  } = data?.prismicBlogPost?.data

  const { tags } = data?.prismicBlogPost || {}

  const keywordArray = keywords ? keywords.split(",") : []

  return (
    <Layout $reverseHeader>
      <Seo
        title={meta_title?.text}
        description={meta_description?.text}
        keywords={keywordArray}
      />
      <div style={{ maxWidth: "1100px", margin: "auto" }}>
        <BlogHeader title={title} category={category} />
        {body && <SliceZone category={category} slices={body} isBlog={true} />}
        <StyledBlogFooter padH $wrappedStandard>
          {tags?.length && <TagNav tags={tags} />}
          <BlogShare />
        </StyledBlogFooter>
        {radio_url && <Radio url={radio_url} />}
        {podcast_links?.length > 0 && (
          <div style={{ padding: "0 2rem 100px 2rem", textAlign: "center" }}>
            <StyledHeading size="h3" style={{ color: "#4A4A4A" }}>
              Listen on your favourite podcast platform
            </StyledHeading>
            <div
              style={{
                display: "flex",
                columnGap: "10px",
                flexWrap: "wrap",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {podcast_links?.map(({ link, link_text }, i) => (
                <AButton
                  key={`podcast-${i}`}
                  href={link?.url}
                  target="_blank"
                  rel="noreferrer"
                >
                  {link_text}
                </AButton>
              ))}
            </div>
          </div>
        )}
      </div>
      {other_blogs?.length > 0 && (
        <OtherBlogs
          articles={data?.allBlogPosts?.nodes}
          otherBlogs={other_blogs}
        />
      )}
    </Layout>
  )
}

export default withPrismicPreview(BlogPost)

export const pageQuery = graphql`
  query postBySlug($uid: String!) {
    prismicBlogPost(uid: { eq: $uid }) {
      _previewable
      tags
      data {
        podcast_links {
          link {
            url
          }
          link_text
        }
        other_blogs {
          blog {
            slug
            document {
              ... on PrismicBlogPost {
                id
                data {
                  title {
                    text
                  }
                  excerpt {
                    html
                  }
                  featured_video_url
                  category {
                    document {
                      ... on PrismicBlogCategory {
                        id
                        data {
                          category_color
                          title {
                            text
                          }
                        }
                      }
                    }
                  }
                }
                url
              }
            }
          }
        }
        title {
          text
        }
        radio_url
        featured_video_url
        meta_title {
          text
        }
        meta_description {
          text
        }
        keywords
        body {
          ... on PrismicBlogPostDataBodyPageSpacer {
            id
            slice_type
            primary {
              size
              block_background
            }
          }
          ... on PrismicBlogPostDataBodyPricingTable {
            id
            slice_type
            primary {
              heading {
                text
              }
              text {
                html
              }
              anchor_id
              link_type
              link_text
              link_target
              page_link {
                link_type
                url
              }
            }
            items {
              title1 {
                text
              }
              sub_heading {
                text
              }
              description {
                html
              }
              package_color
              base_price
              is_poa
            }
          }
          ... on PrismicBlogPostDataBodyVideoBlock {
            id
            slice_type
            primary {
              block_background
              full_video {
                url
              }
              poster_image {
                url
                alt
              }
              anchor_id
            }
          }
          ... on PrismicBlogPostDataBodyMapBlock {
            id
            slice_type
            primary {
              block_background
              location {
                latitude
                longitude
              }
              text {
                html
              }
              page_link {
                link_type
                url
              }
              link_type
              link_target
              link_text
              anchor_id
            }
          }
          ... on PrismicBlogPostDataBodyHubspotForm {
            id
            slice_type
            primary {
              form_id
              heading {
                text
              }
              portal_id
              subheading {
                html
              }
            }
          }
          ... on PrismicBlogPostDataBodyCenteredCta {
            id
            slice_type
            primary {
              block_background
              heading {
                text
              }
              text {
                html
              }
              page_link {
                link_type
                url
              }
              link_type
              link_target
              link_text
              anchor_id
            }
          }
          ... on PrismicBlogPostDataBodyBannerSlider {
            id
            slice_type
            items {
              banner_image {
                url
                alt
              }
              banner_title {
                text
              }
              banner_text {
                html
              }
              link_text
              link_type
              link_target
              page_link {
                url
                link_type
              }
              background_color
              text_color
            }
          }
          ... on PrismicBlogPostDataBodyImageText {
            id
            slice_type
            primary {
              block_background
              mobile_align
              heading {
                text
              }
              text_content {
                html
              }
              image {
                url
                alt
              }
              page_link {
                url
                link_type
              }
              link_type
              link_text
              link_target
              layout
              anchor_id
              quote_block
              video_embed {
                embed_url
              }
            }
          }
          ... on PrismicBlogPostDataBodyFeatures {
            id
            slice_type
            primary {
              block_background
              heading {
                text
              }
              page_link {
                url
                link_type
              }
              link_type
              link_text
              link_target
              align_content
              image_display
              anchor_id
            }
            items {
              feature_title {
                text
              }
              feature_icon {
                url
                alt
              }
              feature_color
              feature_description {
                text
              }
              link_type
              link_target
              link {
                link_type
                url
              }
              link_text
            }
          }
          ... on PrismicBlogPostDataBodyTestimonials {
            id
            slice_type
            items {
              testimonial {
                text
              }
              name {
                text
              }
            }
          }
          ... on PrismicBlogPostDataBodyContactForm {
            id
            slice_type
            primary {
              block_background
              heading {
                text
              }
              text_content {
                html
              }
              confirmation_message {
                html
              }
              include_comments
              anchor_id
            }
          }
          ... on PrismicBlogPostDataBodyMediumBanner {
            id
            slice_type
            primary {
              banner_image {
                url
                alt
              }
              banner_title {
                text
              }
              banner_subtitle {
                text
              }
              background_color
              page_link {
                link_type
                url
              }
              link_type
              link_text
              link_target
              text_color
            }
          }
          ... on PrismicBlogPostDataBodyTextContent {
            id
            slice_type
            primary {
              block_background
              text_content {
                html
                richText
              }
            }
          }

          ... on PrismicBlogPostDataBodyLargeBanner {
            id
            slice_type
            primary {
              banner_image {
                url
                alt
              }
              illustration {
                url
                alt
              }
              banner_title {
                text
              }
              banner_subtitle {
                text
              }
              page_link {
                link_type
                url
              }
              link_type
              link_text
              link_target
              background_color
              text_color
            }
          }

          ... on PrismicBlogPostDataBodyBlogQuote {
            id
            slice_type
            primary {
              text {
                html
                richText
              }
            }
          }
        }

        category {
          document {
            ... on PrismicBlogCategory {
              data {
                title {
                  text
                }
                category_color
              }
            }
          }
        }
      }
    }
    allBlogPosts: allPrismicBlogPost {
      nodes {
        uid
        id
        url
        first_publication_date(formatString: "d MMM Y")
        data {
          featured_video_url
          radio_url
          title {
            text
          }
          excerpt {
            html
          }
          category {
            document {
              ... on PrismicBlogCategory {
                data {
                  title {
                    text
                  }
                  category_color
                }
              }
            }
          }
        }
      }
    }
  }
`
